import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, OnInit } from '@angular/core';
import { LogedUser } from '@base/src/app/core/models/logged-user.model';
import { Constants } from '@base/src/app/models/constants';
import { Overlay } from '@base/src/app/models/overlay.model';
import { QRButton } from '@base/src/app/models/qr-button.model';
import { QRDropdown } from '@base/src/app/models/qr-dropdown.model';
import { QRInput } from '@base/src/app/models/qr-input.model';
import { PhaseBEModel } from '@base/src/app/pages/recruitment/models/phaseBE.model';
import { StageBEModel } from '@base/src/app/pages/recruitment/models/stageBE.model';
import { RecruitmentPhasesService } from '@base/src/app/pages/recruitment/services/phases.service';
import { RecruitmentUtilsService } from '@base/src/app/pages/recruitment/services/recruitment-utils.service';
import { RecruitmentService } from '@base/src/app/pages/recruitment/services/recruitment.service';
import { RecruitmentStagesService } from '@base/src/app/pages/recruitment/services/stages.service';
import { SnackbarService } from '@base/src/app/services/snackbar.service';
import { UtilsService } from '@base/src/app/services/utils.service';

type InputTypes = string | number | undefined;

type DTO = {
  agencyId?: number;
  loggedUser: string;
  stageName?: string;
  phaseName?: string;
  id?: number;
  order?: number;
  removeStageId?: number;
  stageIdTransfer?: number | null;
  phaseIdTransfer?: number | null;
  stageId?: number;
  removePhaseId?: number;
};

type Stage = {
  id: number;
  order: number;
  name: string;
  hierarchy: string;
  selected: boolean;
  hasError: boolean;
};

type Phase = {
  id: number;
  name: string;
  stageId: number;
  hierarchy: string;
  order: number;
};

@Component({
  selector: 'app-config-stages-and-phases',
  templateUrl: './config-stages-and-phases.component.html',
  styleUrls: ['./config-stages-and-phases.component.scss'],
})
export class RecruitmentConfigStagesAndPhasesComponent implements OnInit {
  buttonAdd: QRButton = {
    hierarchy: QRButton.HIERARCHY_3,
    size: QRButton.SIZE_SMALL,
    icon: Constants.ICONS.PLUS,
    viewValue: 'Agregar',
  };

  buttonEdit: QRButton = {
    hierarchy: QRButton.HIERARCHY_4,
    size: QRButton.SIZE_SMALL,
    icon: Constants.ICONS.PENCIL_BLUE,
    iconOnly: true,
  };

  buttonDelete: QRButton = {
    hierarchy: QRButton.HIERARCHY_4,
    size: QRButton.SIZE_SMALL,
    icon: Constants.ICONS.DELETE_2,
    iconOnly: true,
  };

  loading = false;
  user: LogedUser | undefined;

  stages: Stage[] = [];
  phases: Phase[] = [];
  phaseList: Phase[] = [];

  stageName = '';
  phaseName = '';

  blockPhaseEdit = false;

  hasStageSelected = false;

  itemName = '';
  dialogTitle = '';
  dialogAction = '';
  readonly ACTION_ADD = 'add';
  readonly ACTION_EDIT = 'edit';
  readonly ACTION_DELETE = 'delete';
  stageId = 0;
  newStageIdForTransfer: number | null = 0;
  phaseForTransferName = '';
  phaseId = 0;
  order = 0;

  buttonDialogSave: QRButton = {
    hierarchy: QRButton.HIERARCHY_1,
    size: QRButton.SIZE_MEDIUM,
    viewValue: 'Agregar',
  };

  buttonDialogDelete: QRButton = {
    hierarchy: QRButton.HIERARCHY_1,
    size: QRButton.SIZE_MEDIUM,
    viewValue: 'Si, eliminar',
  };

  buttonDialogCancel: QRButton = {
    hierarchy: QRButton.HIERARCHY_3,
    size: QRButton.SIZE_MEDIUM,
    viewValue: 'Cancelar',
  };

  overlay: Overlay = {
    isButtonCloseVisible: false,
    isFullHeight: false,
    doesClickingOutsideContentClose: true,
  };
  isOverlayVisible = false;

  inputAdd: QRInput = {
    mode: QRInput.MODE_STRING,
    hierarchy: QRInput.HIERARCHY_1,
    size: QRInput.SIZE_MEDIUM,
    placeholder: 'Ingrese un nombre',
  };

  dropdownStages: QRDropdown = {
    hierarchy: QRDropdown.HIERARCHY_1,
    size: QRDropdown.SIZE_MEDIUM,
    placeholder: 'Seleccionar',
    options: [],
  };

  dropdownPhases: QRDropdown = {
    hierarchy: QRDropdown.HIERARCHY_1,
    size: QRDropdown.SIZE_MEDIUM,
    placeholder: 'Seleccionar',
    options: [],
  };

  hasCandidates = false;
  hasError = false;

  constructor(
    private readonly stagesService: RecruitmentStagesService,
    private readonly phasesService: RecruitmentPhasesService,
    private readonly utils: UtilsService,
    private readonly recruitmentUtils: RecruitmentUtilsService,
    private readonly snackbarService: SnackbarService,
    private readonly recruitmentService: RecruitmentService
  ) {}

  ngOnInit(): void {
    this.loading = true;
    this.user = this.utils.getLoggedUser();
    if (this.user) this.load();
  }

  load() {
    if (this.user) {
      const AGENCY_ID = this.user.agency_id.toString();
      this.getStages(AGENCY_ID);
    }
  }

  loadOnlyPhases() {
    const AGENCY_ID = this.user?.agency_id.toString() ?? '';
    this.stages.forEach((e: Stage) => {
      this.getPhases(AGENCY_ID, e.id);
    });
  }

  async getStages(agencyId: string) {
    this.stages = [];
    this.stagesService.get(agencyId).then((response: any) => {
      if (response?.data?.stages) {
        let index = 0;
        response?.data?.stages.forEach((e: StageBEModel) => {
          index = index + 1;
          const STAGE = {
            id: e.id,
            order: e.order,
            name: e.name,
            hierarchy: this.recruitmentUtils.generateStagesHierarchy(
              index,
              response?.data?.stages.length
            ),
            selected: false,
            hasError: false,
          };
          this.stages.push(STAGE);
          this.getPhases(agencyId, e.id);
        });
      }
      this.stages.sort((a: Stage, b: Stage) => (a.order > b.order ? 1 : -1));

      this.loading = false;
    });
  }

  getPhases(agencyId: string, stageId: number) {
    this.phases = [];
    this.phaseList = [];
    const PHASE_ID = agencyId + '/' + stageId.toString();
    this.phasesService.get(PHASE_ID).then((response: any) => {
      if (response?.data?.phases) {
        response.data.phases.forEach((e: PhaseBEModel) => {
          e.stageId = stageId;
          const PHASE = {
            id: e.id,
            name: e.name,
            stageId: e.stageId,
            hierarchy: e.hierarchy ?? '',
            order: 0,
          };
          this.phases.push(PHASE);
        });
        const FIND_STAGE = this.stages.find((e: Stage) => e.selected);
        if (FIND_STAGE) {
          this.onSelectionStage(FIND_STAGE.id);
        }
        this.checkForErrorInStages();
      }
    });
  }

  checkForErrorInStages() {
    let valid = true;
    this.stages.forEach((stage: Stage) => {
      const FIND = this.phases.find(
        (phase: Phase) => phase.stageId == stage.id
      );
      stage.hasError = !FIND && stage.name != 'Contratado';
      valid = valid && !stage.hasError;
    });
    this.hasError = !valid;
  }

  onSelectionStage(id: number) {
    this.hasStageSelected = false;
    this.phaseList = [];
    this.stages.forEach((e: Stage) => {
      e.selected = false;
      if (e.id == id) {
        this.blockPhaseEdit =
          (e.name == 'Nuevo' && e.order == 1) || e.name == 'Contratado';
        e.selected = true;
        this.hasStageSelected = true;
      }
    });
    this.phaseList = this.phases.filter((e: Phase) => e.stageId == id);
    const STAGE = this.stages.find((e: Stage) => e.id == id);
    let index = 0;
    this.phaseList.forEach((e: Phase) => {
      index = index + 1;
      e.hierarchy = STAGE?.hierarchy ?? '';
      e.order = index;
    });
  }

  changeInputAdd($event: InputTypes) {
    if ($event) {
      const RESULT = $event.toString();
      this.validateName(RESULT);
    } else {
      this.itemName = '';
      this.inputAdd.hint = '0/30';
    }
  }

  validateName(result: string) {
    if (result.length <= 29) {
      this.itemName = result;
      this.inputAdd.hint = (this.itemName.length ?? '0') + '/30';
    } else {
      this.snackbarService.openSnackBar(
        'El máximo permitido de caracteres es 30',
        ''
      );
    }
  }

  onSave(blockReload?: boolean) {
    if (
      (!this.itemName && !this.hasCandidates) ||
      (this.itemName == this.stageName && this.hasCandidates) ||
      (this.itemName == this.phaseName && this.hasCandidates)
    ) {
      this.snackbarService.openSnackBar('Complete los campos requeridos', '');
    } else {
      this.loading = true;
      const URL = this.dialogTitle.includes('etapa') ? 'stage' : 'phase';
      const DTO: DTO = this.generateDTO(URL);
      switch (this.dialogAction) {
        case this.ACTION_ADD: {
          this.recruitmentService.post(DTO, URL).then((response: any) => {
            if (response?.body?.code == 200) {
              this.snackbarService.openSnackBar(response.body.message, '');
              URL == 'stage' ? this.load() : this.loadOnlyPhases();
            }
          });
          break;
        }
        case this.ACTION_EDIT: {
          this.recruitmentService.put(DTO, URL).then((response: any) => {
            if (response?.body?.code == 200) {
              this.snackbarService.openSnackBar(response.body.message, '');
              if (!blockReload) {
                URL == 'stage' ? this.load() : this.loadOnlyPhases();
              }
            }
          });
          break;
        }
        case this.ACTION_DELETE: {
          const NEW_URL = URL + '/' + this.ACTION_DELETE;
          this.recruitmentService.post(DTO, NEW_URL).then((response: any) => {
            if (response?.body?.code == 200) {
              this.snackbarService.openSnackBar(response.body.message, '');
              URL == 'stage' ? this.load() : this.loadOnlyPhases();
            }
          });
          break;
        }
      }
      this.loading = false;
      this.isOverlayVisible = false;
    }
  }

  generateDTO(url: string): DTO {
    let dto: DTO = { loggedUser: this.user?.username ?? '' };
    switch (this.dialogAction) {
      case this.ACTION_ADD: {
        // Crear etapa
        if (url == 'stage') {
          dto = {
            agencyId: this.user?.agency_id,
            loggedUser: this.user?.username ?? '',
            stageName: this.itemName,
          };
          // Crear fase
        } else {
          dto = {
            stageId: this.stageId,
            loggedUser: this.user?.username ?? '',
            phaseName: this.itemName,
          };
        }
        break;
      }
      case this.ACTION_EDIT: {
        if (url == 'stage') {
          dto = {
            id: this.stageId,
            agencyId: this.user?.agency_id,
            loggedUser: this.user?.username ?? '',
            stageName: this.itemName,
            order: this.order,
          };
        } else {
          dto = {
            id: this.phaseId,
            stageId: this.stageId,
            loggedUser: this.user?.username ?? '',
            phaseName: this.itemName,
            order: this.order,
          };
        }
        break;
      }
      case this.ACTION_DELETE: {
        const STAGE_TRANSFER = this.stages.find(
          (e: Stage) => e.name == this.itemName
        );
        if (url == 'stage') {
          dto = {
            agencyId: this.user?.agency_id,
            loggedUser: this.user?.username ?? '',
            removeStageId: this.stageId,
            stageIdTransfer: STAGE_TRANSFER?.id,
            phaseIdTransfer: this.phaseId,
          };
        } else {
          const PHASE_TRANSFER = this.phases.find(
            (e: Phase) => e.name == this.itemName && e.stageId == this.stageId
          );
          dto = {
            loggedUser: this.user?.username ?? '',
            removePhaseId: this.phaseId,
            stageIdTransfer: PHASE_TRANSFER?.stageId,
            phaseIdTransfer: PHASE_TRANSFER?.id,
          };
        }
        break;
      }
    }
    return dto;
  }

  onAddStage() {
    if (this.stages.length >= 10) {
      this.snackbarService.openWithVariableDuration(
        'El proceso de reclutamiento permite un MÁXIMO de 10 etapas.',
        '',
        4000
      );
    } else {
      this.dialogAction = this.ACTION_ADD;
      this.dialogTitle = 'Agregar etapa';
      this.isOverlayVisible = true;
      this.itemName = '';
      this.stageId = 0;
      this.phaseId = 0;
      this.inputAdd.hint = (this.itemName.length ?? 0) + '/30';
      this.hasCandidates = false;
    }
  }

  onAddPhase() {
    if (!this.blockPhaseEdit) {
      const STAGE_SELECTED = this.stages.filter((e: Stage) => e.selected);
      if (this.phaseList.length >= 5) {
        this.snackbarService.openWithVariableDuration(
          'El proceso de reclutamiento permite un MÁXIMO de 5 fases.',
          '',
          4000
        );
      } else if (!STAGE_SELECTED || STAGE_SELECTED.length === 0) {
        this.snackbarService.openWithVariableDuration(
          'Debe tener seleccionada una etapa para poder agregar una fase a la misma.',
          '',
          4000
        );
      } else {
        const STAGE = this.stages.find((e: Stage) => e.selected);
        this.dialogAction = this.ACTION_ADD;
        this.dialogTitle = 'Agregar fase';
        this.isOverlayVisible = true;
        this.itemName = '';
        this.stageId = STAGE?.id ?? 0;
        this.phaseId = 0;
        this.inputAdd.hint = this.itemName.length + '/30';
        this.hasCandidates = false;
      }
    } else {
      this.snackbarService.openWithVariableDuration(
        'No se permite agregar fases a esta etapa',
        '',
        4000
      );
    }
  }

  onEditStage(stage: Stage, event: MouseEvent) {
    event.stopPropagation();
    this.isOverlayVisible = true;
    this.dialogAction = this.ACTION_EDIT;
    this.stageId = stage.id;
    this.phaseId = 0;
    this.itemName = stage.name;
    this.dialogTitle = 'Modificar etapa';
    this.order = stage.order;
    this.inputAdd.hint = (this.itemName.length ?? 0) + '/30';
    this.hasCandidates = false;
  }

  onEditPhase(phase: Phase, event: MouseEvent) {
    event.stopPropagation();
    this.isOverlayVisible = true;
    this.dialogAction = this.ACTION_EDIT;
    this.stageId = phase.stageId;
    this.phaseId = phase.id;
    this.itemName = phase.name;
    this.inputAdd.hint = (this.itemName.length ?? 0) + '/30';
    this.dialogTitle = 'Modificar fase';
    this.order = phase.order;
    this.hasCandidates = false;
  }

  async onDeleteStage(stage: Stage, event: MouseEvent) {
    event.stopPropagation();
    const RESPONSE = await this.verifyStagesCandidates(stage.id);
    this.hasCandidates = RESPONSE?.data ?? false;
    this.stageName = stage.name;
    if (this.hasCandidates) {
      this.generateOptionsStages(stage.id);
    }
    this.dialogAction = this.ACTION_DELETE;
    this.stageId = stage.id;
    this.phaseId = 0;
    this.itemName = stage.name;
    this.dialogTitle = 'Eliminar etapa';
    this.order = stage.order;
    this.isOverlayVisible = true;
  }

  async onDeletePhase(phase: Phase, event: MouseEvent) {
    event.stopPropagation();
    this.newStageIdForTransfer = null;
    const RESPONSE = await this.verifyPhasesCandidates(phase.id);
    this.hasCandidates = RESPONSE?.data ?? false;
    this.phaseName = phase.name;
    this.stageId = phase.stageId;
    if (this.hasCandidates) {
      this.generateOptionsPhases(phase.id);
    }
    this.dialogAction = this.ACTION_DELETE;
    this.phaseId = phase.id;
    this.itemName = phase.name;
    this.dialogTitle = 'Eliminar fase';
    this.order = phase.order;
    this.isOverlayVisible = true;
  }

  dropStage(event: CdkDragDrop<Stage[]>) {
    if (
      event.currentIndex === 0 ||
      event.currentIndex === this.stages.length - 1 ||
      event.currentIndex == event.previousIndex
    ) {
      return;
    } else {
      this.order = event.currentIndex + 1;
      moveItemInArray(this.stages, event.previousIndex, event.currentIndex);
      this.editStageOrder(this.stages[event.currentIndex]);
    }
  }

  dropPhase(event: CdkDragDrop<Stage[]>) {
    this.order = event.currentIndex + 1;
    moveItemInArray(this.phaseList, event.previousIndex, event.currentIndex);
    this.editPhaseOrder(this.phaseList[event.currentIndex]);
  }

  editStageOrder(stage: Stage) {
    this.itemName = stage.name;
    this.stageId = stage.id;
    this.dialogTitle = 'etapa';
    this.dialogAction = this.ACTION_EDIT;
    this.onSave(true);
  }

  editPhaseOrder(phase: Phase) {
    this.itemName = phase.name;
    this.stageId = phase.stageId;
    this.phaseId = phase.id;
    this.dialogTitle = 'fase';
    this.dialogAction = this.ACTION_EDIT;
    this.onSave(true);
  }

  verifyStagesCandidates(id: number) {
    const URL = id.toString() + '/verify';
    return this.stagesService.get(URL);
  }

  verifyPhasesCandidates(id: number) {
    const URL = id.toString() + '/verify';
    return this.phasesService.get(URL);
  }

  generateOptionsStages(id: number) {
    this.dropdownStages.options = [];

    this.stages.forEach((e: Stage) => {
      if (e.id != id && e.name != 'Contratado') {
        this.dropdownStages.options.push(e.name);
      }
    });
    this.generateOptionsDropdownPhaseList(id);
  }

  generateOptionsPhases(id: number) {
    this.dropdownPhases.options = [];
    this.phaseList.forEach((e: Phase) => {
      if (e.id != id) {
        this.dropdownPhases.options.push(e.name);
      }
    });
  }

  generateOptionsDropdownPhaseList(stageId: number) {
    this.newStageIdForTransfer = stageId;
    this.dropdownPhases.options = [];
    const PHASES = this.phases.filter((e: Phase) => e.stageId == stageId);
    PHASES.forEach((e: Phase) => {
      this.dropdownPhases.options.push(e.name);
    });
  }

  dropdownStagesChange($event: string) {
    this.dropdownPhases.options = [];
    this.itemName = $event;
    const STAGE = this.stages.find((e: Stage) => e.name == $event);
    if (STAGE) {
      this.generateOptionsDropdownPhaseList(STAGE.id);
    }
  }

  dropdownPhasesChange($event: string) {
    if (this.newStageIdForTransfer) {
      this.phaseForTransferName = $event;
    } else {
      this.itemName = $event;
    }

    const STAGE_ID = this.newStageIdForTransfer ?? this.stageId;

    const PHASE = this.phases.find(
      (e: Phase) => e.name == $event && e.stageId == STAGE_ID
    );
    this.phaseId = PHASE?.id ?? 0;
  }

  onKeyDown(e: KeyboardEvent) {
    if (this.itemName.length >= 30 && !this.isDeleteOrBackspace(e)) {
      e.preventDefault();
    }
  }

  isDeleteOrBackspace(event: KeyboardEvent): boolean {
    return event.key === 'Backspace' || event.key === 'Delete';
  }
}
